import React, {useEffect, useMemo, useState} from "react";
import freePlan from "../../../../assets/icon-free-plan.svg";
import proPlan from "../../../../assets/icon-pro-plan.svg";
import enterprisePlan from "../../../../assets/icon-interprise-plan.svg";
import websiteIcon from "../../../../assets/websiteIcon.svg";
import orderIcon from "../../../../assets/orderIcon.svg";
import bgCard from "../../../../assets/bg-card.png";
import {FiCheckSquare} from "react-icons/fi";
import {PricingStyles} from "./PricingSection.style";
import {useNavigate} from "react-router-dom";
import subscriptionService from "services/subscriptionService";
import { useAuthContext } from "Context/authContext";
import { useSelector } from "react-redux";
import Loaders from "components/atoms/Loaders";

const planIconByName = (name = "") => {
    const lower = name.toLowerCase();
    if (lower.includes("starter")) return freePlan;
    if (lower.includes("growth")) return proPlan;
    if (lower.includes("premium")) return enterprisePlan;
    return proPlan;
};

const PricingSection = ({ businessIdFromUrl }) => {
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const businessIdFromAuth = user?.businessId;
    const businessIdFromRedux = useSelector((state) => state?.business?.businessId);
    const businessId = businessIdFromUrl || businessIdFromAuth || businessIdFromRedux;

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [error, setError] = useState("");
    const [plans, setPlans] = useState([]);
    const [checkingOutId, setCheckingOutId] = useState("");

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setError("");
            setLoadingPlans(true);
            try {
                console.log('Attempting to fetch subscription plans...');
                const data = await subscriptionService.getPlans();
                console.log('Plans fetched successfully:', data);
                // Expecting data like { data: { monthlyPlan: [], yearlyPlan: [] } } or flat array
                const normalized = Array.isArray(data?.data)
                    ? data.data
                    : [
                          ...(data?.monthlyPlan || data?.data?.monthlyPlan || []),
                          ...(data?.yearlyPlan || data?.data?.yearlyPlan || []),
                      ];
                const orderIndex = (name = "") => {
                    const n = (name || "").toLowerCase();
                    if (n.includes("starter")) return 0;
                    if (n.includes("growth")) return 1;
                    if (n.includes("premium")) return 2;
                    return 3;
                };
                const sorted = [...normalized].sort((a, b) => {
                    const aName = a?.productName || a?.product?.name || "";
                    const bName = b?.productName || b?.product?.name || "";
                    return orderIndex(aName) - orderIndex(bName);
                });
                if (mounted) setPlans(sorted);
            } catch (e) {
                console.error('Failed to fetch plans:', e);
                if (mounted) setError(e.message || "Failed to load plans");
            } finally {
                if (mounted) setLoadingPlans(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, []);

    const handleChoose = async (plan) => {
        const normalizedBusinessId = Array.isArray(businessId) ? businessId[0] : businessId;
        if (!normalizedBusinessId) {
            navigate("/login");
            return;
        }
        try {
            setError("");
            const priceId = plan?.priceId || plan?.id || plan?.price?.id || plan?.stripePriceId;
            if (!priceId) throw new Error("No priceId found for selected plan");
            setCheckingOutId(priceId);
            const res = await subscriptionService.createCheckout({ businessId: normalizedBusinessId, priceId });
            const checkoutUrl = res?.checkoutUrl || res?.data?.checkoutUrl;
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
                return;
            }
            throw new Error("Invalid checkout response");
        } catch (e) {
            setError(e.message || "Unable to start checkout");
            setCheckingOutId("");
        }
    };

    return (
        <PricingStyles>
            <div className="container">
                {error && (
                    <div style={{ color: "red", marginBottom: 16 }}>{error}</div>
                )}
                <Loaders loading={loadingPlans && plans.length === 0} height="60vh">
                    <div className="pricing-holder">
                        {plans.map((plan, index) => {
                            const productName = plan?.productName || plan?.product?.name || "Plan";
                            const interval = plan?.interval || plan?.recurring?.interval || "month";
                            const amountCents = plan?.unit_amount ?? plan?.amount ?? 0;
                            const amount = amountCents / 100;
                            const icon = planIconByName(productName);
                            const popular = index === 1;
                            const isSelected = selectedIndex === index;
                            const priceId = plan?.priceId || plan?.id || plan?.price?.id || plan?.stripePriceId;
                            const isThisCheckingOut = checkingOutId === priceId;
                            return (
                                <div
                                    key={plan.id || plan.priceId || index}
                                    className={`col ${isSelected ? "proPlan" : ""}`}
                                    data-aos={popular ? "fade-up" : undefined}
                                    onClick={() => setSelectedIndex(index)}
                                    style={
                                        isSelected
                                            ? {
                                                  backgroundImage: `url(${bgCard})`,
                                                  backgroundRepeat: "no-repeat",
                                                  backgroundSize: "cover",
                                              }
                                            : {}
                                    }>
                                    <div className="header">
                                        {popular && <span className="tag">Most Popular</span>}
                                        <div className="icon-box">
                                            <img src={icon} width="60" alt="icon" />
                                        </div>
                                        <strong className="title">{productName}</strong>
                                        <p>Flexible subscription</p>
                                        <div className="price-box">
                                            <sup>From</sup>
                                            <strong className="price">${amount}</strong>
                                            <sub> / {interval}</sub>
                                        </div>
                                        <div className="no-card">
                                            <button className="btn" type="button" disabled={!!checkingOutId} onClick={() => handleChoose(plan)}>
                                                {isThisCheckingOut ? "Redirecting..." : `Choose ${productName}`}
                                            </button>
                                            <ul className="list-wrap">
                                                <li className="li">
                                                    <img src={websiteIcon} alt="icon" />
                                                    Branded website
                                                </li>
                                                <li className="li">
                                                    <img src={orderIcon} alt="icon" />
                                                    Online ordering platform
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                    <ul className="info-list">
                                        {["Menu management", "Loyalty and retention", "Reporting and analytics", "Integrated payments"].map(
                                            (item, i) => (
                                                <li key={i}>
                                                    <FiCheckSquare size={16} color="rgba(148, 148, 148, 1)" />
                                                    <span className="text">{item}</span>
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </Loaders>
            </div>
        </PricingStyles>
    );
};

export default PricingSection;
